// =============================================================================
// bubbles/MediaBubble.tsx — Bolha de mídia (image, video, audio, voice, document).
//
// - image: thumbnail clicável (abre em nova aba)
// - video: thumbnail com ícone play
// - audio/voice: player nativo <audio>
// - document: ícone de arquivo + nome inferido da URL
//
// F46-S11 ("Anexar aos documentos"): a ação/estado vem de `attach` (prop),
// NUNCA monta seu próprio hook aqui — ver header de MessageBubble.tsx e de
// features/conversations/hooks/useAttachMediaAsDocument.ts.
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../../../../lib/cn';
import type { Message, MessageType } from '../../../types';
import { StatusIcon } from '../StatusIcon';
import { fileNameFromUrl, formatBubbleTime } from '../utils';

import { AudioPlayer } from './AudioPlayer';
import { useMediaAvailability } from './mediaAvailability';
import { MediaLoadingPlaceholder, MediaUnavailablePlaceholder } from './MediaPlaceholders';

/**
 * Estado + ação de "Anexar aos documentos" para ESTA mensagem — computado e
 * mantido UMA vez em `MessageBubble.tsx` (o dispatcher, montado uma vez por
 * mensagem pelo painel) e repassado por prop. Nunca monte
 * `useAttachMediaAsDocument`/`useDocumentsGate`/`useConversation` aqui
 * dentro — precedente do live chat: contador duplicado por hook de socket
 * montado duas vezes (useConversationSocket.ts).
 */
export interface MediaAttachState {
  /** flag `documents.enabled` ainda resolvendo — não pisca nada enquanto isso. */
  isLoading: boolean;
  /** flag ligada E ator tem `documents:write`. */
  canAttach: boolean;
  /** null = conversa sem lead vinculado — a ação não pode agir, mas precisa
   * explicar (nunca sumir em silêncio, nota do slot). */
  leadId: string | null;
  /** Documento já vinculado a esta mensagem, ou null. */
  attachedDocumentId: string | null;
  /**
   * F46-S35: `false` quando esta mídia nunca teve arquivo guardado
   * (`media_storage_key` nula — mensagem anterior à migration 0143,
   * 04/09/2026) e `POST /api/documents` responderia 422 permanente, sem
   * backfill possível. A ação nem aparece nesse caso — nunca oferecer algo
   * que só pode falhar.
   */
  canAttachAsDocument: boolean;
  onOpenDialog: () => void;
}

interface MediaBubbleProps {
  message: Message;
  isOutbound: boolean;
  /** Ausente = feature indisponível para este contexto (ex.: bubble usada
   * fora do live chat real, ou testes que não montam o gate). */
  attach?: MediaAttachState | undefined;
}

// ─── Ícones inline ───────────────────────────────────────────────────────────

function IconImage(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-5 h-5"
    >
      <rect x="2" y="2" width="16" height="16" rx="3" />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" stroke="none" />
      <path d="M2 13l5-4 4 4 2-2 5 5" strokeLinejoin="round" />
    </svg>
  );
}

function IconVideo(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-5 h-5"
    >
      <rect x="1" y="4" width="13" height="12" rx="2" />
      <path d="M14 8l5-3v10l-5-3V8z" />
    </svg>
  );
}

function IconAudio(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-5 h-5"
    >
      <path d="M9 4v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1h3a1 1 0 011 1z" />
      <path d="M9 7l7 3-7 3V7z" />
    </svg>
  );
}

function IconDocument(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      className="w-5 h-5"
    >
      <path d="M4 2h8l4 4v12a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" />
      <path d="M12 2v4h4M7 9h6M7 12h6M7 15h4" strokeLinecap="round" />
    </svg>
  );
}

function IconPlay(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 opacity-90">
      <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.5)" stroke="white" strokeWidth="1" />
      <path d="M10 8.5l6 3.5-6 3.5V8.5z" fill="white" />
    </svg>
  );
}

/** Clipe — ícone da ação "Anexar aos documentos". */
function IconClip({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      className={className}
      aria-hidden="true"
    >
      <path
        d="M13.5 6.5l-5.8 5.8a2.2 2.2 0 003.1 3.1l6.2-6.2a3.8 3.8 0 00-5.4-5.4L5.4 10a5.4 5.4 0 007.6 7.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Check — indicador "Anexado aos documentos". */
function IconCheck({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Mapa de ícones por tipo ──────────────────────────────────────────────────

type MediaType = Extract<MessageType, 'image' | 'video' | 'audio' | 'voice' | 'document'>;

const mediaIcon: Record<MediaType, React.ReactNode> = {
  image: <IconImage />,
  video: <IconVideo />,
  audio: <IconAudio />,
  voice: <IconAudio />,
  document: <IconDocument />,
};

const mediaLabel: Record<MediaType, string> = {
  image: 'Imagem',
  video: 'Vídeo',
  audio: 'Áudio',
  voice: 'Mensagem de voz',
  document: 'Documento',
};

// ─── "Anexar aos documentos" — overlay (sobre imagem/vídeo) ───────────────────
//
// Sempre visível (nunca atrás de `:hover`) — a operação usa celular, onde
// hover não existe (nota do slot). Botão SIBLING da <a> da mídia (nunca
// aninhado dentro dela) — evita elemento interativo dentro de outro
// elemento interativo (anti-padrão de acessibilidade) e o navegador nunca
// tenta navegar a âncora ao clicar aqui.

function AttachOverlayButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Anexar aos documentos"
      title="Anexar aos documentos"
      className={cn(
        'absolute top-1.5 right-1.5 z-10',
        'w-9 h-9 flex items-center justify-center rounded-pill',
        'bg-[rgba(10,18,40,0.55)] text-white backdrop-blur-[2px]',
        'transition-all duration-fast',
        'hover:bg-[rgba(10,18,40,0.75)] hover:-translate-y-0.5',
        'active:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
      )}
    >
      <IconClip className="w-4 h-4" />
    </button>
  );
}

function AttachedOverlayBadge({ leadId }: { leadId: string | null }): React.JSX.Element {
  const content = (
    <span
      className={cn(
        'absolute top-1.5 left-1.5 z-10',
        'inline-flex items-center gap-1 rounded-pill px-2 py-1',
        'bg-[rgba(10,18,40,0.55)] text-white backdrop-blur-[2px]',
        'font-sans text-[11px] font-semibold',
      )}
    >
      <IconCheck className="w-3 h-3" />
      Anexado
    </span>
  );

  if (!leadId) return content;

  return (
    <Link
      to={`/crm/${leadId}`}
      aria-label="Anexado aos documentos — ver na ficha do lead"
      title="Anexado aos documentos — ver na ficha do lead"
      className={cn(
        'absolute top-1.5 left-1.5 z-10',
        'inline-flex items-center gap-1 rounded-pill px-2 py-1',
        'bg-[rgba(10,18,40,0.55)] text-white backdrop-blur-[2px]',
        'font-sans text-[11px] font-semibold',
        'hover:bg-[rgba(10,18,40,0.75)] transition-colors duration-fast',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
      )}
    >
      <IconCheck className="w-3 h-3" />
      Anexado
    </Link>
  );
}

// ─── "Anexar aos documentos" — inline (áudio/documento, sem superfície de
// imagem para sobrepor) ────────────────────────────────────────────────────

function AttachInlineButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 -mx-2',
        'font-sans text-xs font-semibold text-azul',
        'hover:bg-azul/10 transition-colors duration-fast',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
      )}
    >
      <IconClip className="w-3.5 h-3.5" />
      Anexar aos documentos
    </button>
  );
}

function AttachedInlineBadge({ leadId }: { leadId: string | null }): React.JSX.Element {
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      <IconCheck className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />
      Anexado aos documentos
    </span>
  );
  const cls = 'inline-flex items-center gap-1.5 font-sans text-xs font-semibold text-ink-2';

  if (!leadId) return <span className={cls}>{inner}</span>;

  return (
    <Link
      to={`/crm/${leadId}`}
      className={cn(
        cls,
        'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/30 rounded-xs',
      )}
    >
      {inner}
    </Link>
  );
}

/**
 * Quando a conversa não tem lead vinculado, a ação não pode agir — mas
 * precisa EXPLICAR, não sumir em silêncio (nota do slot: o vínculo
 * automático inbound→lead está morto em produção, conversa sem lead é
 * comum). `title` cobre mouse; o toggle por clique cobre toque (celular não
 * tem hover para revelar um `title`).
 */
function AttachNoLeadHint({ inline }: { inline: boolean }): React.JSX.Element {
  const [expanded, setExpanded] = React.useState(false);
  const label = 'Vincule esta conversa a um lead para anexar documentos';

  const button = (
    <button
      type="button"
      onClick={() => setExpanded((s) => !s)}
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      className={cn(
        inline
          ? 'inline-flex items-center gap-1.5 rounded-sm px-2 py-1.5 -mx-2 font-sans text-xs font-semibold text-ink-2'
          : cn(
              'absolute top-1.5 right-1.5 z-10 w-9 h-9 flex items-center justify-center rounded-pill',
              'bg-[rgba(10,18,40,0.4)] text-white/70 backdrop-blur-[2px]',
            ),
        'transition-colors duration-fast',
        'focus-visible:outline-none focus-visible:ring-2',
        inline ? 'focus-visible:ring-azul/30' : 'focus-visible:ring-white/70',
      )}
    >
      <IconClip className={inline ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      {inline && 'Anexar aos documentos'}
    </button>
  );

  if (!expanded) return button;

  return (
    <>
      {button}
      <p
        role="status"
        className={cn(
          'font-sans text-xs text-ink-2',
          inline ? 'mt-1' : 'absolute top-11 right-1.5 z-10 max-w-[200px] text-right',
        )}
        style={
          !inline
            ? {
                background: 'var(--bg-elev-1)',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 8px',
                boxShadow: 'var(--elev-2)',
              }
            : undefined
        }
      >
        {label}.
      </p>
    </>
  );
}

/** Decide o que renderizar no lugar da ação — resolve OVERLAY vs INLINE por
 * fora, aqui só decide QUAL estado mostrar (nada/anexar/anexado/sem lead). */
/**
 * Decide QUAL estado a ação "Anexar aos documentos" deve mostrar — extraída
 * como função pura (testada em __tests__/attach-to-documents.test.tsx sem
 * precisar montar React; este monorepo não usa @testing-library/react, ver
 * header de HandoffBriefingModal.test.ts) porque é exatamente aqui que vive
 * o comportamento exigido pela Definition of Done do slot: "some sem lead
 * vinculado, sem documents:write ou com flag OFF".
 */
export type AttachAffordanceKind = 'hidden' | 'attached' | 'no-lead' | 'action';

export function resolveAttachAffordanceKind(
  attach: MediaAttachState | undefined,
): AttachAffordanceKind {
  if (!attach || attach.isLoading || !attach.canAttach) return 'hidden';
  if (attach.attachedDocumentId) return 'attached';
  // F46-S35: mídia sem arquivo guardado (media_storage_key nula) nunca
  // oferece a ação — o 422 é permanente e não há retry possível. Checado
  // DEPOIS de "attached": uma mensagem já anexada continua mostrando o
  // badge mesmo que, hipoteticamente, `canAttachAsDocument` viesse `false`.
  if (!attach.canAttachAsDocument) return 'hidden';
  if (!attach.leadId) return 'no-lead';
  return 'action';
}

function AttachAffordance({
  attach,
  inline,
}: {
  attach: MediaAttachState | undefined;
  inline: boolean;
}): React.JSX.Element | null {
  const kind = resolveAttachAffordanceKind(attach);

  if (kind === 'hidden' || !attach) return null;

  if (kind === 'attached') {
    return inline ? (
      <AttachedInlineBadge leadId={attach.leadId} />
    ) : (
      <AttachedOverlayBadge leadId={attach.leadId} />
    );
  }

  if (kind === 'no-lead') {
    return <AttachNoLeadHint inline={inline} />;
  }

  return inline ? (
    <AttachInlineButton onClick={attach.onOpenDialog} />
  ) : (
    <AttachOverlayButton onClick={attach.onOpenDialog} />
  );
}

// ─── Componente ──────────────────────────────────────────────────────────────

export function MediaBubble({ message, isOutbound, attach }: MediaBubbleProps): React.JSX.Element {
  const type = message.type as MediaType;
  const isImage = type === 'image';
  const isVideo = type === 'video';
  const isAudio = type === 'audio' || type === 'voice';
  const isDocument = type === 'document';

  // F58-S14: prazo de 15 s sem `mediaUrl` e uma única nova tentativa de URL
  // assinada quando o arquivo falha — ver bubbles/mediaAvailability.ts.
  const { phase, onMediaError } = useMediaAvailability(message.conversationId, message.mediaUrl);

  const bubbleBase = cn(
    'relative max-w-full rounded-md overflow-hidden',
    'font-sans text-sm',
    '[box-shadow:var(--elev-1)]',
    isOutbound
      ? [
          'self-end',
          'bg-[color-mix(in_srgb,var(--brand-azul)_12%,var(--bg-elev-1))]',
          'border border-[color-mix(in_srgb,var(--brand-azul)_20%,transparent)]',
          'rounded-br-xs',
        ]
      : ['self-start', 'bg-surface-2 border border-border-subtle', 'rounded-bl-xs'],
  );

  const footer = (
    <div
      className={cn(
        'flex items-center gap-1 px-3 pb-2 pt-1',
        isOutbound ? 'justify-end' : 'justify-start',
      )}
    >
      <time
        dateTime={message.createdAt}
        className="font-sans text-xs text-ink-3 tabular-nums"
        title={new Date(message.createdAt).toLocaleString('pt-BR')}
      >
        {formatBubbleTime(message.createdAt)}
      </time>
      {isOutbound && message.viewStatus && <StatusIcon status={message.viewStatus} size={12} />}
    </div>
  );

  // ── Indisponível (URL venceu e a nova também falhou, ou nunca veio) ───────
  // Documento fica de fora: ele não depende da mídia carregar para renderizar
  // (mostra nome + "Baixar arquivo" quando há URL).
  if (phase === 'unavailable' && !isDocument && type in mediaIcon) {
    return (
      <MediaUnavailablePlaceholder
        icon={mediaIcon[type]}
        label={mediaLabel[type].toLowerCase()}
        footer={footer}
        className={cn(bubbleBase, 'px-3 py-3')}
      />
    );
  }

  // ── Imagem ──────────────────────────────────────────────────────────────────
  if (isImage && message.mediaUrl) {
    return (
      <div className={cn(bubbleBase, 'p-0')}>
        <a
          href={message.mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir imagem em nova aba"
          className="block"
        >
          <img
            src={message.mediaUrl}
            alt={message.content ?? 'Imagem'}
            loading="lazy"
            onError={onMediaError}
            className="max-w-[280px] max-h-[300px] object-cover w-full"
          />
        </a>
        <AttachAffordance attach={attach} inline={false} />
        {message.content && (
          <p className="px-3 pt-2 text-ink whitespace-pre-wrap break-words">{message.content}</p>
        )}
        {footer}
      </div>
    );
  }

  // ── Vídeo ───────────────────────────────────────────────────────────────────
  if (isVideo && message.mediaUrl) {
    return (
      <div className={cn(bubbleBase, 'p-0')}>
        <a
          href={message.mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir vídeo em nova aba"
          className="relative block bg-black"
        >
          <video
            src={message.mediaUrl}
            preload="metadata"
            onError={onMediaError}
            className="max-w-[280px] max-h-[220px] object-cover w-full"
            muted
          />
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <IconPlay />
          </span>
        </a>
        <AttachAffordance attach={attach} inline={false} />
        {message.content && (
          <p className="px-3 pt-2 text-ink whitespace-pre-wrap break-words">{message.content}</p>
        )}
        {footer}
      </div>
    );
  }

  // ── Áudio / Voz ─────────────────────────────────────────────────────────────
  if (isAudio && message.mediaUrl) {
    return (
      <div className={cn(bubbleBase, 'px-3 pt-3')}>
        <AudioPlayer src={message.mediaUrl} isOutbound={isOutbound} onError={onMediaError} />
        <div className="mt-1.5">
          <AttachAffordance attach={attach} inline />
        </div>
        {footer}
      </div>
    );
  }

  // ── Documento ────────────────────────────────────────────────────────────────
  if (isDocument) {
    const fileName = fileNameFromUrl(message.mediaUrl);
    const href = message.mediaUrl ?? undefined;
    return (
      <div className={cn(bubbleBase, 'px-3 pt-3')}>
        <div className="flex items-center gap-3 mb-1">
          <span className="text-ink-3 shrink-0">{mediaIcon[type]}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink truncate max-w-[180px]">{fileName}</p>
            <p className="text-xs text-ink-3">{mediaLabel[type]}</p>
          </div>
        </div>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-azul hover:underline mb-1 transition-colors"
            download
          >
            Baixar arquivo
          </a>
        )}
        <div className="mb-1">
          <AttachAffordance attach={attach} inline />
        </div>
        {footer}
      </div>
    );
  }

  // ── Carregando (media_url ainda null) ────────────────────────────────────
  // O worker baixa a mídia de forma assíncrona (F45-S04): message:new chega
  // primeiro, sem media_url. Skeleton — nunca ícone quebrado nem espaço vazio
  // (DS anti-padrão "loading engasgado") — até message:media_ready atualizar
  // o cache (ver useConversationSocket) ou o prazo de 15 s estourar e a bolha
  // virar "Mídia indisponível" (F58-S14). Sem `AttachAffordance` aqui: mídia
  // ainda sem `mediaUrl` não tem o que anexar.
  const knownMediaType = type in mediaIcon;
  if (knownMediaType && !message.mediaUrl) {
    return (
      <MediaLoadingPlaceholder
        icon={mediaIcon[type]}
        label={mediaLabel[type].toLowerCase()}
        footer={footer}
        className={cn(bubbleBase, 'px-3 py-3')}
      />
    );
  }

  // Fallback genérico (tipo desconhecido)
  return (
    <div className={cn(bubbleBase, 'px-3 py-3')}>
      <div className="flex items-center gap-2 text-ink-3">
        <span>{mediaIcon[type] ?? <IconDocument />}</span>
        <span className="text-xs">{mediaLabel[type] ?? 'Mídia'} (sem prévia)</span>
      </div>
      {footer}
    </div>
  );
}
